<template>
    <div data-testid="client-onboarding-walkthrough">
        <VJoyWalkthrough
            ref="vJoyWalkthroughRef"
            data-target="client-onboarding-walkthrough-expanded"
            data-testid="client-onboarding-walkthrough-expanded"
            trigger-icon="list"
            :trigger-title="t('client.onboarding.checklist.title.top')"
            :footer="t('client.onboarding.checklist.close')"
            :trigger-subtitle="walkthroughSubtitle"
            :trigger-attrs="{
                'data-testid': 'client-onboarding-walkthrough-collapsed',
                ...(walkthroughNeedsOffset ? {class: `client-onboarding-walkthrough-collapsed-offset`} : {}),
            }"
            :open="expand"
            @hide:footer="closeForever"
        >
            <template #walkthrough-illustration>
                <MaltIllustration class="onboarding-central-hub__img" name="man-rising-hand--high" alt="" width="50" />
            </template>
            <template #walkthrough-title>{{ t('client.onboarding.checklist.title', {0: user?.username.split(' ')[0]}) }}</template>
            <template #walkthrough-subtitle>{{ t('client.onboarding.checklist.subtitle') }}</template>
            <template #walkthrough-progress>
                <VJoyProgressBar data-testid="client-onboarding-progress-bar" :percentage="jobCompletionPercentage" color="success" />
            </template>
            <template #walkthrough-content>
                <VJoyCollapse v-model="collapseState" mode="default" :accordion="true">
                    <VJoyCollapseItem
                        v-if="jobsGroup.includes('searchForFreelancer')"
                        id="search-freelancer"
                        data-testid="client-onboarding-search-for-freelancer"
                        :header="t('client.onboarding.checklist.group.1.title')"
                        :open="groupToExpandWhenJobFinished === 'searchForFreelancer' || firstJobNotDoneGroupToExpand === 'searchForFreelancer'"
                    >
                        <JobToBeDone
                            v-if="jobsCurrentSteps.get('findProfileUsingAi')"
                            icon="sparkles"
                            title="client.onboarding.checklist.group.1.path.2.title"
                            data-testid="client-onboarding-find-profile-using-ai-job"
                            job="findProfileUsingAi"
                            :button-state="jobsCurrentSteps.get('findProfileUsingAi')!.buttonState"
                            :job-just-finished="jobJustFinished"
                            @start="start('findProfileUsingAi')"
                            @confetti-animation-triggered="jobJustFinished = null"
                        />

                        <JobToBeDone
                            v-if="jobsCurrentSteps.get('browseProfiles')"
                            icon="view-search"
                            title="client.onboarding.checklist.group.1.path.1.title"
                            data-testid="client-onboarding-browse-profiles-job"
                            job="browseProfiles"
                            :button-state="jobsCurrentSteps.get('browseProfiles')!.buttonState"
                            :job-just-finished="jobJustFinished"
                            @start="start('browseProfiles')"
                            @confetti-animation-triggered="jobJustFinished = null"
                        />
                    </VJoyCollapseItem>

                    <VJoyCollapseItem
                        v-if="jobsGroup.includes('workWithYourOwnFreelancers')"
                        id="work-with-network"
                        data-testid="client-onboarding-work-with-your-own-freelancers"
                        :header="t('client.onboarding.checklist.group.2.title')"
                        :open="
                            groupToExpandWhenJobFinished === 'workWithYourOwnFreelancers' ||
                            firstJobNotDoneGroupToExpand === 'workWithYourOwnFreelancers'
                        "
                    >
                        <JobToBeDone
                            v-if="jobsCurrentSteps.get('companyFreelancers')"
                            icon="company-placeholder"
                            title="client.onboarding.checklist.group.2.path.1.title"
                            data-testid="client-onboarding-company-freelancers-job"
                            job="companyFreelancers"
                            :button-state="jobsCurrentSteps.get('companyFreelancers')!.buttonState"
                            :job-just-finished="jobJustFinished"
                            @start="start('companyFreelancers')"
                            @confetti-animation-triggered="jobJustFinished = null"
                        />

                        <JobToBeDone
                            v-if="jobsCurrentSteps.get('inviteFreelancer')"
                            icon="user-lighter"
                            title="client.onboarding.checklist.group.2.path.2.title"
                            data-testid="client-onboarding-invite-freelancer-job"
                            job="inviteFreelancer"
                            :button-state="jobsCurrentSteps.get('inviteFreelancer')!.buttonState"
                            :redirect-url="jobsCurrentSteps.get('inviteFreelancer')!.redirectUrl"
                            :job-just-finished="jobJustFinished"
                            @start="start('inviteFreelancer')"
                            @confetti-animation-triggered="jobJustFinished = null"
                        />
                    </VJoyCollapseItem>

                    <VJoyCollapseItem
                        v-if="jobsGroup.includes('connectWithIndependentConsultants')"
                        id="discover-malt-strategy"
                        data-testid="client-onboarding-connect-with-independent-consultants"
                        :header="t('client.onboarding.checklist.group.3.title')"
                        :open="
                            groupToExpandWhenJobFinished === 'connectWithIndependentConsultants' ||
                            firstJobNotDoneGroupToExpand === 'connectWithIndependentConsultants'
                        "
                    >
                        <JobToBeDone
                            icon="star"
                            title="client.onboarding.checklist.group.3.path.1.title"
                            data-testid="client-onboarding-discover-malt-strategy-job"
                            job="discoverMaltStrategy"
                            :button-state="jobsCurrentSteps.get('discoverMaltStrategy')!.buttonState"
                            :redirect-url="jobsCurrentSteps.get('discoverMaltStrategy')!.redirectUrl"
                            :job-just-finished="jobJustFinished"
                            @start="start('discoverMaltStrategy')"
                            @confetti-animation-triggered="jobJustFinished = null"
                        />
                    </VJoyCollapseItem>
                </VJoyCollapse>
            </template>
        </VJoyWalkthrough>

        <OnboardingProductTour
            v-if="currentComponentToBeShown && productTourTargetPresent && currentComponentToBeShown.type === 'ProductTour'"
            v-bind="currentComponentToBeShown"
            @closed="onProductClosed"
        />

        <OnboardingDialog
            v-if="currentComponentToBeShown && currentComponentToBeShown.type === 'Dialog'"
            v-bind="currentComponentToBeShown"
            @closed="onProductClosed"
        />
    </div>
</template>

<script setup lang="ts">
    import {computed, ref, useTemplateRef, watch, onMounted, nextTick} from 'vue';
    import {navbarEventBus} from '@navbar/public-api';
    import {storeToRefs} from 'pinia';
    import {useRoute} from 'vue-router';
    import {MaltIllustration} from '#components';
    import {useTranslation, useAuth, useLogger} from '#imports';
    import {onClickOutside, safelyNavigateTo, useDisplayMobile} from '@malt/utils';
    import {launchConfetti} from '@malt/vue-animation';
    import {VJoyProgressBar, VJoyCollapse, VJoyCollapseItem, VJoyWalkthrough} from '@maltjoy/core-vue';
    import JobToBeDone from './JobToBeDone.vue';
    import OnboardingDialog from './OnboardingDialog.vue';
    import OnboardingProductTour from './OnboardingProductTour.vue';
    import {useClientOnboardingTracking} from '../composables/useClientOnboardingTracking';
    import type {ComponentInformation} from '../jobsSteps';
    import type {ClientOnboardingJobs} from '../openapi';
    import {useOnboardingStore} from '../stores/onboardingClient.store';

    const route = useRoute();

    const {isDisplayMobile} = useDisplayMobile();
    const {t} = useTranslation();
    const {user} = storeToRefs(useAuth());

    const {trackClientOnboardingJobStarted, trackClientOnboardingClosed} = useClientOnboardingTracking();

    const walkthroughNeedsOffset = ref<boolean>(false);

    onMounted(async () => {
        walkthroughNeedsOffset.value =
            document.querySelector('[data-target-search-sidebar-action-button]') !== null ||
            document.querySelector('[data-client-onboarding-propose-a-project]') !== null;

        if (/keyword/i.test(route.query.search_mode as string)) {
            await start('browseProfiles');
            if (currentComponentToBeShown?.value && currentComponentToBeShown.value.type === 'ProductTour') {
                waitForProductTargetOrFail(currentComponentToBeShown.value?.target);
            }
        }
    });

    const onboardingStore = useOnboardingStore();
    const {isWalkthroughExpanded, jobsCurrentSteps, allJobsFinished, jobCompletionPercentage} = storeToRefs(onboardingStore);

    const collapseState = ref<[]>([]);
    const vJoyWalkthroughRef = useTemplateRef('vJoyWalkthroughRef');

    const walkthroughSubtitle = computed(() =>
        isDisplayMobile.value
            ? `${jobCompletionPercentage.value}%`
            : t('client.onboarding.checklist.percentage.complete', {0: `${jobCompletionPercentage.value}%`}),
    );

    const expand = ref(isWalkthroughExpanded.value);
    watch(
        () => vJoyWalkthroughRef.value?.isOpen,
        () => {
            expand.value = !!vJoyWalkthroughRef.value?.isOpen;
        },
    );

    const nextComponentToBeShown = computed<ComponentInformation | undefined>(() => {
        const jobs = [...jobsCurrentSteps.value.values()];
        const components = jobs.map((job) => job?.component).filter((component) => component !== undefined);

        return components.find((component) => {
            if (!component!.path) return true;
            return window.location.pathname.includes(component!.path);
        });
    });

    const currentComponentToBeShown = ref(nextComponentToBeShown.value);
    watch(
        currentComponentToBeShown,
        async () => {
            if (currentComponentToBeShown.value) {
                if (currentComponentToBeShown.value.beforeOpen) {
                    currentComponentToBeShown.value.beforeOpen();
                }

                const job = currentComponentToBeShown.value.job;
                await onboardingStore.startOrAdvanceJob(job);

                if (currentComponentToBeShown.value.type === 'ProductTour') {
                    waitForProductTargetOrFail(currentComponentToBeShown.value.target);
                }
            }
        },
        {immediate: true},
    );

    type Group = 'searchForFreelancer' | 'workWithYourOwnFreelancers' | 'connectWithIndependentConsultants';
    const groupToExpandWhenJobFinished = ref<Group>();

    const jobsGroup = computed(() => [...jobsCurrentSteps.value.keys()].map((step) => getGroupOf(step)));

    const firstJobNotDoneGroupToExpand = computed((): Group | undefined => {
        const firstJobNotDone = [...jobsCurrentSteps.value.keys()].find((job) => jobsCurrentSteps.value.get(job)?.buttonState !== 'DONE');

        if (firstJobNotDone) {
            return getGroupOf(firstJobNotDone);
        }

        return undefined;
    });

    function getGroupOf(job: keyof ClientOnboardingJobs): Group | undefined {
        if (['findProfileUsingAi', 'browseProfiles'].includes(job)) {
            return 'searchForFreelancer';
        }

        if (['inviteFreelancer', 'companyFreelancers'].includes(job)) {
            return 'workWithYourOwnFreelancers';
        }

        if (['discoverMaltStrategy'].includes(job)) {
            return 'connectWithIndependentConsultants';
        }

        return undefined;
    }

    async function start(job: keyof ClientOnboardingJobs) {
        await onboardingStore.startOrAdvanceJob(job);
        await trackClientOnboardingJobStarted(job, jobCompletionPercentage.value);
        const hasBeenRedirected = await redirectIfNotOnGoodPage(job);

        if (!hasBeenRedirected) {
            expand.value = false;
            await switchToNextComponent();
        }
    }

    async function redirectIfNotOnGoodPage(job: keyof ClientOnboardingJobs) {
        const componentToStart = jobsCurrentSteps.value.get(job)!.component;

        if (!componentToStart) {
            return false;
        }

        if (componentToStart.path) {
            const areWeOnTheGoodPage = window.location.pathname.includes(componentToStart.path);

            if (!areWeOnTheGoodPage) {
                await safelyNavigateTo({path: componentToStart.path}, {externalToThisApp: true});
                return true;
            }
        }

        if (!componentToStart.path) {
            const isNavbarElementPresent = document.querySelector('[data-navbar-aside]') !== null;

            if (!isNavbarElementPresent) {
                await safelyNavigateTo({path: '/project-client/find-freelancers'}, {externalToThisApp: true});
                return true;
            }
        }

        return false;
    }

    const jobJustFinished = ref<keyof ClientOnboardingJobs | null>(null);

    function onJobFinished(job: keyof ClientOnboardingJobs) {
        expand.value = true;
        jobJustFinished.value = job;
        groupToExpandWhenJobFinished.value = getGroupOf(jobJustFinished.value);
    }

    async function switchToNextComponent() {
        // Set to undefined to close the previous productTour and then open the new one
        currentComponentToBeShown.value = undefined;
        await nextTick();
        currentComponentToBeShown.value = nextComponentToBeShown.value;
    }

    async function onProductClosed() {
        const job = currentComponentToBeShown.value?.job;
        const buttonState = jobsCurrentSteps.value.get(job!)?.buttonState;
        const isJobFinished = buttonState === 'DONE';
        if (isJobFinished) {
            onJobFinished(job!);

            // SetTimeout used because we want to run the all jobs done confetti a bit later than the job just finished confetti
            setTimeout(() => {
                launchConfettiInOnboardingWalkthroughIfNeeded();
            }, 500);
        }

        await switchToNextComponent();
    }

    function launchConfettiInOnboardingWalkthroughIfNeeded() {
        const expandedWalkthroughHTMLElement = document.querySelector('[data-target="client-onboarding-walkthrough-expanded"]') as HTMLElement;

        if (allJobsFinished.value && expandedWalkthroughHTMLElement) {
            launchConfetti(expandedWalkthroughHTMLElement, {
                dataTestid: 'canvas-all-jobs-done-confetti',
                confettiOptions: {
                    particleCount: 300,
                    origin: {y: 1.25},
                    startVelocity: 70,
                },
            });
        }
    }

    async function closeForever() {
        await onboardingStore.closeForever();
        await trackClientOnboardingClosed(jobCompletionPercentage.value);
        navbarEventBus.emit('request:navbar-header-get-started-on-malt-onboarding');
    }

    const walkthroughHTMLElement = ref();
    onMounted(() => (walkthroughHTMLElement.value = document.querySelector('.joy-walkthrough') as HTMLElement));
    onClickOutside(walkthroughHTMLElement, () => (expand.value = false));

    const logger = useLogger();
    const productTourTargetPresent = ref(false);
    // We need to wait for the target to be shown mostly because of microfrontend loading time
    // And fail if not present to detect removed product tour target (as we chose not to write end-to-end tests)
    function waitForProductTargetOrFail(target: string) {
        const CHECK_INTERVAL_IN_MS = 1000;
        const MAX_ATTEMPTS = 4;

        let attempts = 0;

        const checkTarget = () => {
            const isTargetPresent = document.querySelector(`[${target}]`) !== null;

            if (isTargetPresent) {
                productTourTargetPresent.value = true;
                return true;
            } else {
                attempts++;
                if (attempts >= MAX_ATTEMPTS) {
                    logger.error(`Client onboarding product tour target [${target}] not found`);
                    return true;
                }
            }
            return false;
        };

        if (!checkTarget()) {
            const interval = setInterval(() => {
                if (checkTarget()) {
                    clearInterval(interval);
                }
            }, CHECK_INTERVAL_IN_MS);
        }
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .client-onboarding-walkthrough-collapsed-offset {
        @include mq.screen_xs {
            bottom: 85px !important;
        }
    }
</style>
